/**
 * pdf.js im Browser: Legacy-Build (läuft sicher auf iPadOS 17), Worker und alle
 * Ressourcen lokal ausgeliefert – nichts vom CDN, kein eval (pdf.js ≥ 5).
 */
import type { PDFDocumentLoadingTask, PDFDocumentProxy, PDFPageProxy } from "pdfjs-dist/legacy/build/pdf.mjs";
import workerUrl from "pdfjs-dist/legacy/build/pdf.worker.min.mjs?url";
import { apiUrl } from "../api/client.js";

export type { PDFDocumentProxy, PDFPageProxy };

// eslint-disable-next-line @typescript-eslint/consistent-type-imports -- reiner Typ des dynamisch geladenen Moduls
type PdfJs = typeof import("pdfjs-dist/legacy/build/pdf.mjs");

let pdfjsPromise: Promise<PdfJs> | null = null;

function pdfjs(): Promise<PdfJs> {
  pdfjsPromise ??= import("pdfjs-dist/legacy/build/pdf.mjs").then((mod) => {
    mod.GlobalWorkerOptions.workerSrc = workerUrl;
    return mod;
  });
  return pdfjsPromise;
}

const assetBase = (dir: string) => new URL(`pdfjs/${dir}/`, document.baseURI).href;

export interface LoadedPdf {
  pdf: PDFDocumentProxy;
  destroy: () => Promise<void>;
}

/** Lädt ein Dokument per Range-Requests (der Server liefert 206 + ETag). */
export async function loadPdf(
  documentId: string,
  onProgress?: (fraction: number) => void,
): Promise<LoadedPdf> {
  const lib = await pdfjs();
  const task: PDFDocumentLoadingTask = lib.getDocument({
    url: apiUrl(`api/documents/${encodeURIComponent(documentId)}/file`),
    withCredentials: true,
    cMapUrl: assetBase("cmaps"),
    cMapPacked: true,
    standardFontDataUrl: assetBase("standard_fonts"),
    wasmUrl: assetBase("wasm"),
    iccUrl: assetBase("iccs"),
    disableAutoFetch: false,
    rangeChunkSize: 256 * 1024,
    enableXfa: false,
  });
  if (onProgress) {
    task.onProgress = ({ loaded, total }: { loaded: number; total: number }) => {
      if (total > 0) onProgress(Math.min(1, loaded / total));
    };
  }
  const pdf = await task.promise;
  return { pdf, destroy: () => task.destroy() };
}

/** iPad-Safari begrenzt Canvas-Fläche; darüber wird die Auflösung reduziert. */
export const MAX_CANVAS_PIXELS = 5_000_000;

export interface RenderHandle {
  promise: Promise<void>;
  cancel: () => void;
}

/** Rendert eine Seite in ein Canvas mit Breite `cssWidth` (CSS-Pixel). */
export function renderPage(page: PDFPageProxy, canvas: HTMLCanvasElement, cssWidth: number): RenderHandle {
  const base = page.getViewport({ scale: 1 });
  let scale = (cssWidth / base.width) * Math.min(window.devicePixelRatio || 1, 3);
  const pixels = base.width * base.height * scale * scale;
  if (pixels > MAX_CANVAS_PIXELS) scale *= Math.sqrt(MAX_CANVAS_PIXELS / pixels);
  const viewport = page.getViewport({ scale });

  // Erst in ein Offscreen-Canvas rendern, dann umkopieren → kein weißes Flackern
  const buffer = document.createElement("canvas");
  buffer.width = Math.floor(viewport.width);
  buffer.height = Math.floor(viewport.height);
  // Weiß wie Papier im PDF-Standard: die Noten erscheinen in ihren Originalfarben, ohne Tönung.
  const task = page.render({ canvas: buffer, viewport, background: "#FFFFFF" });
  const promise = task.promise.then(() => {
    canvas.width = buffer.width;
    canvas.height = buffer.height;
    canvas.getContext("2d")?.drawImage(buffer, 0, 0);
    buffer.width = buffer.height = 0;
  });
  return {
    promise,
    cancel: () => {
      task.cancel();
      buffer.width = buffer.height = 0;
    },
  };
}
